<template>
  <div class="cp-sidebar__main" :class="{'loading': loading}">
    <div class="order-tracking">
      <ProcessingLoader v-if="loading" />
      <!-- Main Content -->
      <div class="order-tracking__content" v-if="item && itemFulfillments.length" :class="{'d-none': loading}">
        <!-- Left Panel - Tracking Details -->
        <div class="order-tracking__left-panel" v-if="tracking">
          <div class="tracking-info">
            <!-- Tracking Header -->
            <div class="tracking-info__header">
              <div class="tracking-info__carrier">
                <div class="tracking-info__carrier-logo-container">
                  <img :src="require(`@/assets/images/trackinglogos/${tracking.carrier}.png`)" class="carrier-logo" />
                </div>
                <div class="tracking-info__tracking-number">
                  <div>Tracking#</div>
                  <div class="tracking-number" v-for="fulfillment in itemFulfillments" v-bind:key="'track-'+fulfillment.tracking_number" @click="updateSelectedFulfillment(fulfillment)">
                    <span>{{ fulfillment.tracking_number }}</span>
                    <svg v-if="itemFulfillments.length > 1 && selectedFulfillment.id === fulfillment.id" class="d-none d-lg-block" xmlns="http://www.w3.org/2000/svg" width="9" height="15" viewBox="0 0 9 15" fill="none">
                      <path d="M1.705 0.243164L0 1.94816L5.54 7.50016L0 13.0522L1.705 14.7572L8.962 7.50016L1.705 0.243164Z" fill="#1C1C1C"/>
                    </svg>
                  </div>
                </div>
                <svg @click.stop="toggleScrollbar()" xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none" class="d-block d-md-none cursor-pointer">
                  <g filter="url(#filter0_d_2439_55251)">
                    <g filter="url(#filter1_d_2439_55251)">
                      <circle cx="23" cy="19" r="19" fill="#F4F4F4"/>
                    </g>
                    <path d="M30 26L16 12L30 26ZM30 12L16 26L30 12Z" fill="#FAFAFA"/>
                    <path d="M30 26L16 12M30 12L16 26" stroke="#797979" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </g>
                  <defs>
                    <filter id="filter0_d_2439_55251" x="0" y="0" width="46" height="46" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dy="4"/>
                      <feGaussianBlur stdDeviation="2"/>
                      <feComposite in2="hardAlpha" operator="out"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"/>
                      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2439_55251"/>
                      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2439_55251" result="shape"/>
                    </filter>
                  </defs>
                </svg>
              </div>
              <div class="tracking-info__delivery_status_container">
                <div class="tracking-info__wrapper">
                  <span class="tracking-info__wrapper__heading"><span v-if="!isDelivered">Estimated</span> Delivery <span v-if="isDelivered">Date</span></span>
                  <span class="tracking-info__wrapper__description">{{ estimatedDeliveryDate }}</span>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="2" height="47" viewBox="0 0 2 47" fill="none">
                  <path d="M1 0.5L0.999998 46.5" stroke="#CCCCCC" stroke-linecap="round"/>
                </svg>
                <div class="tracking-info__wrapper">
                  <span class="tracking-info__wrapper__heading">Status</span>
                  <span class="tracking-info__wrapper__description status-badge" :class="getStatusClass(tracking.status)">
                    {{ statusText }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Shipping Details Timeline -->
            <div class="tracking-info__timeline">
              <div class="timeline-header">
                Shipping Details
              </div>
              <div ref="timelineContainer" class="timeline-container">
                <div class="timeline">
                  <div
                    v-for="(item, index) in details"
                    :key="index"
                    class="timeline-item"
                  >
                    <!-- Vertical line and dot -->
                    <div class="timeline-marker">
                      <div
                        class="timeline-line"
                        :class="{ active: index >= activeIndex}"
                      ></div>
                      <div
                        class="timeline-dot"
                        :class="{ active: index >= activeIndex , completed: index >= activeIndex }"
                      ></div>
                    </div>

                    <!-- Content -->
                    <div class="timeline-content">
                      <div class="timeline-content__heading">
                        <h3>{{ item.status }}</h3>
                        <p class="location"><span v-if="item.location">{{ item.location }} -</span> {{ item.date }}, {{item.time}}</p>
                      </div>
                      <div v-if="item.events && item.events.length" class="events">
                        <div v-for="(event, i) in item.events" :key="i" class="event">
                          <ul>
                            <li>
                              <div class="event-time">{{ event.date }}, {{event.time}}</div>
                              <div class="event-description">{{ event.message }}</div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Button -->
            <div class="tracking-info__actions">
              <b-link class="seemore" variant="secondary" @click="toggleAllTimeline($event)">See more</b-link>
              <button class="btn-base input-choice-btn input-btn stroke-2x" @click="toggleScrollbar()">
                Return to Order
              </button>
            </div>
          </div>
        </div>

        <!-- Right Panel - Map -->
        <div class="order-tracking__right-panel">
          <div class="map-container"><div id="map" ref="mapContainer"></div></div>
        </div>
      </div>
      <div class="centered-block" v-else-if="!loading && !tracking">
        <div class="step-title">Invalid tracking ID or couldn't find any data</div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {getHumanReadableDate, getShortTime} from '@/utils'
import {TrackingStatus, TrackingStatusStrings} from '@/constants'
import axios from 'axios'
import ProcessingLoader from '@/components/ProcessingLoader.vue'

export default {
  name: 'OrderTracking',
  components: {ProcessingLoader},
  props: {
    item: {
      type: Object,
      required: true
    },
    fulfillments: {
      type: Array,
      default: () => []
    }
  },
  data: function() {
    return {
      map: null,
      markers: [],
      tracking: null,
      currentIndex: -1,
      selectedFulfillment: null,
      itemFulfillments: [],
      loading: true,
      mobileFitBounds: false,
      infoWindow: null
    }
  },
  computed: {
    ...mapState(['styleVariables', 'session']),
    latestTrackingEvent: function() {
      if (this.details && this.details.length) {
        return {
          ...this.details[0],
          date: getHumanReadableDate(this.details[0].scan_date, true),
          time: getShortTime(this.details[0].scan_date)
        }
      }
      return null
    },
    statusText: function() {
      return TrackingStatusStrings[this.tracking.status]
    },
    isDelivered: function() {
      return this.tracking.status === TrackingStatus.Delivered
    },
    details: function() {
      let details = {}

      for (let detail of this.tracking.details) {
        let location = ''
        let country = detail.country || 'US'

        if (detail.city && detail.state) {
          location = detail.city + ', ' + detail.state + ', ' + country
        }

        location = location || detail.location || ''
        let message = detail.message || ''

        if (detail.status === TrackingStatus.Unknown) {
          detail.status = TrackingStatus.InTransit
        }

        if (detail.status === TrackingStatus.InTransit) {
          message = message + (message.endsWith('in') ? ' ' : ', ') + location
        } else {
          message = location + ' - ' + message
        }

        if (!details[detail.status]) {
          details[detail.status.toString()] = {
            date: getHumanReadableDate(detail.scan_date, true),
            time: getShortTime(detail.scan_date),
            scan_date: detail.scan_date,
            location: location,
            status: this.getStatusText(detail.status),
            message: message,
            events: []
          }
        } else {
          details[detail.status.toString()]['events'].push({
            location: location,
            scan_date: detail.scan_date,
            date: getHumanReadableDate(detail.scan_date, true),
            time: getShortTime(detail.scan_date),
            message: message
          })
        }
      }

      return (Object.values(details) || [])
        .map(d => {
          d.events.sort((a, b) => new Date(b.scan_date) - new Date(a.scan_date))
          return d
        })
        .sort((a, b) => new Date(b.scan_date) - new Date(a.scan_date))
    },
    estimatedDeliveryDate: function() {
      let estimatedDeliveryDate = this.tracking.est_delivery_date
      if (!estimatedDeliveryDate && this.tracking.status === TrackingStatus.Delivered) {
        estimatedDeliveryDate = this.tracking.details[0].scan_date
      }
      return getHumanReadableDate(estimatedDeliveryDate, true)
    },
    activeIndex: function() {
      return this.tracking.status === this.details[0].status
    },
    packageLocationIcon: function() {
      return {
        url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
        <svg width="54" height="54" viewBox="0 0 54 54" xmlns="http://www.w3.org/2000/svg">
          <path opacity="0.35" d="M27.0573 53.4934C41.6613 53.4934 53.5003 41.6545 53.5003 27.0504C53.5003 12.4464 41.6613 0.607422 27.0573 0.607422C12.4532 0.607422 0.614258 12.4464 0.614258 27.0504C0.614258 41.6545 12.4532 53.4934 27.0573 53.4934Z" fill="${this.styleVariables['theme_color']}"/>
          <path d="M27.0591 39.7003C34.0444 39.7003 39.7071 34.0376 39.7071 27.0523C39.7071 20.067 34.0444 14.4043 27.0591 14.4043C20.0738 14.4043 14.4111 20.067 14.4111 27.0523C14.4111 34.0376 20.0738 39.7003 27.0591 39.7003Z" fill="#71AC00"/>
          <path d="M27.0591 38.2003C33.216 38.2003 38.2071 33.2092 38.2071 27.0523C38.2071 20.8954 33.216 15.9043 27.0591 15.9043C20.9023 15.9043 15.9111 20.8954 15.9111 27.0523C15.9111 33.2092 20.9023 38.2003 27.0591 38.2003Z" fill="${this.styleVariables['theme_color']}" stroke="white" stroke-width="3"/>
        </svg>
      `),
        anchor: new window.google.maps.Point(27, 27)
      }
    },
    deliveryLocationIcon: function() {
      return {
        url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
          <svg width="41" height="56" viewBox="0 0 41 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.1 0C14.7712 0.00661555 9.66251 2.12641 5.89446 5.89446C2.12641 9.66251 0.00661555 14.7712 0 20.1C0 33.847 17.985 54.041 18.75 54.894C18.9197 55.083 19.1274 55.2341 19.3593 55.3375C19.5913 55.441 19.8425 55.4944 20.0965 55.4944C20.3505 55.4944 20.6017 55.441 20.8337 55.3375C21.0656 55.2341 21.2733 55.083 21.443 54.894C22.208 54.044 40.193 33.852 40.193 20.1C40.1864 14.7724 38.0676 9.66478 34.301 5.89693C30.5345 2.12908 25.4276 0.00846713 20.1 0ZM20.1 30.208C18.1001 30.208 16.1451 29.6149 14.4823 28.5038C12.8195 27.3926 11.5236 25.8133 10.7584 23.9656C9.99318 22.1179 9.79312 20.0848 10.1835 18.1233C10.5739 16.1619 11.5372 14.3603 12.9515 12.9464C14.3659 11.5325 16.1677 10.5697 18.1293 10.1799C20.0908 9.79012 22.1239 9.99078 23.9714 10.7565C25.8189 11.5223 27.3978 12.8187 28.5084 14.4818C29.6191 16.145 30.2116 18.1001 30.211 20.1C30.2102 22.7811 29.1446 25.3521 27.2485 27.2476C25.3524 29.1431 22.7811 30.208 20.1 30.208Z" fill="${this.styleVariables['theme_color']}"/>
          </svg>
        `)
      }
    }
  },
  methods: {
    toggleScrollbar: function() {
      this.$emit('clear')
    },
    getStatusText: function(status) {
      return TrackingStatusStrings[status]
    },
    toggleAllTimeline: function(e) {
      const showMore = e.currentTarget.textContent === 'See more'
      if (this.$refs && this.$refs.timelineContainer) {
        if (showMore) {
          this.$refs.timelineContainer.classList.add('unset-height')
          e.currentTarget.textContent = 'See less'
        } else {
          this.$refs.timelineContainer.classList.remove('unset-height')
          e.currentTarget.textContent = 'See more'
        }
      }
    },
    geocodeAddress: function(address, isDelivery = false) {
      return new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            // TODO: save these co-ordinates on order level
            if (isDelivery) {
              this.deliveryLocation = {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng()
              }
            } else {
              this.packageLocation = {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng()
              }
            }
            resolve()
          } else {
            // eslint-disable-next-line reject,prefer-promise-reject-errors
            reject()
          }
        })
      })
    },
    initMap() {
      // Create the map
      this.map = new window.google.maps.Map(this.$refs.mapContainer, {
        center: this.packageLocation,
        zoom: 14,
        disableDefaultUI: true,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      })

      // Create package marker
      // eslint-disable-next-line no-new
      new window.google.maps.Marker({
        position: this.packageLocation,
        map: this.map,
        icon: this.packageLocationIcon
      })

      // Create delivery marker
      // eslint-disable-next-line no-new
      const deliveryMarker = new window.google.maps.Marker({
        position: this.deliveryLocation,
        map: this.map,
        icon: this.deliveryLocationIcon
      })

      const infowindow = new window.google.maps.InfoWindow({
        headerDisabled: true,
        content: `
            <div class="marker-address">
              <p class="heading">${this.latestTrackingEvent.location}</p>
              <p class="description">${this.latestTrackingEvent.date}, ${this.latestTrackingEvent.time}</p>
            </div>
        `,
        position: this.deliveryLocation,
        // move it left and up relative to the marker’s anchor
        pixelOffset: new window.google.maps.Size(-110, 20)
      })
      infowindow.open(this.map, deliveryMarker)

      // Draw path between markers
      const path = new window.google.maps.Polyline({
        path: [this.packageLocation, this.deliveryLocation],
        geodesic: true,
        strokeOpacity: 0,
        strokeWeight: 3,
        icons: [{
          icon: {
            path: 'M 0,-1 0,1',
            strokeOpacity: 1,
            scale: 4,
            strokeColor: this.styleVariables['theme_color']
          },
          offset: '0',
          repeat: '15px'
        }]
      })
      path.setMap(this.map)

      // Fit bounds to show both markers
      const bounds = new window.google.maps.LatLngBounds()
      bounds.extend(this.packageLocation)
      bounds.extend(this.deliveryLocation)
      this.map.fitBounds(bounds)

      window.addEventListener('resize', () => {
        if (this.map) {
          const map = this.map
          this.map.fitBounds(bounds)

          if (window.innerWidth < 992) {
            // Estimate InfoWindow dimensions
            const iwOuter = document.querySelector('.gm-style-iw')
            if (!iwOuter) return

            const iwHeight = iwOuter.offsetHeight
            const iwWidth = iwOuter.offsetWidth

            const projection = map.getProjection()
            if (!projection) return

            const markerPos = projection.fromLatLngToPoint(deliveryMarker.getPosition())
            const offsetY = (iwHeight / 2 + 10) / Math.pow(2, map.getZoom())
            const offsetX = (iwWidth / 2 + 10) / Math.pow(2, map.getZoom())
            const newCenter = new window.google.maps.Point(markerPos.x - offsetX, markerPos.y - offsetY)
            map.setCenter(projection.fromPointToLatLng(newCenter))
          }
        }
      })
    },
    getStatusClass: function(status) {
      const statusMap = {
        'Out For Delivery': 'out-for-delivery',
        'In-Transit': 'in-transit',
        'Delivered': 'delivered',
        'Pending': 'pending'
      }
      return statusMap[status] || 'pending'
    },
    getTrackingData() {
      if (this.item) {
        if (!this.fulfillments || !this.fulfillments.length) {
          this.loading = true
          axios.get(`/order_items/${this.item.id}/fulfillments/`).then(response => {
            const trackAbleFulfillment = response.data.find(item => item.can_track)
            if (trackAbleFulfillment) {
              this.itemFulfillments = response.data.filter((item) => item.can_track)
              this.updateSelectedFulfillment(trackAbleFulfillment)
            }
          }, () => {
            this.loading = false
          })
        } else {
          this.itemFulfillments = this.fulfillments
          this.updateSelectedFulfillment(this.itemFulfillments[0])
        }
      }
    },
    updateSelectedFulfillment(fulfillment) {
      if (this.selectedFulfillment && this.selectedFulfillment.id === fulfillment.id) return
      this.selectedFulfillment = fulfillment
      if (!this.tracking) {
        axios.get('/fulfillments/' + this.selectedFulfillment.id.toString() + '/').then(async(result) => {
          this.tracking = result.data.tracking
          const customer = this.session.customer
          const customerAddress = customer.ship_address1 + ', ' + customer.ship_city
          try {
            await this.geocodeAddress(customerAddress, !!result.data.rma_number)
            await this.geocodeAddress(this.latestTrackingEvent.location, !result.data.rma_number)
            this.loading = false
            this.initMap()
          } catch (e) {
            console.error('GEOCODING ERROR:' + e)
          }
        }, () => {
          this.loading = false
        })
      } else {
        this.loading = false
      }
    }
  },
  mounted() {
    this.getTrackingData()
  }
}
</script>

<style scoped lang="less">
  .order-tracking {
    width: 100%;
    display: flex;
    background: #FFFFFF;
    flex-direction: column;
    font-family: 'Open Sans', sans-serif;

    &__content {
      display: flex;
      gap: 20px;
      padding: 0;
      margin: 0 auto;
      width: 100%;
      flex-direction: column-reverse;

      @media (min-width: 992px) {
        flex-direction: row;
      }
    }

    &__left-panel {
      flex: 1;
      max-width: none;
      position: relative;

      @media (min-width: 992px) {
        max-width: 344px;
        height: calc(100vh - 50px);       // full viewport
        position: relative;   // keep for absolute child
      }
    }

    &__right-panel {
      position: relative;
      @media (min-width: 992px) {
        min-height: 300px;
        flex: 1;
      }
    }
  }

  .tracking-info {
    padding: 20px 25px;
    border-radius: 20px 20px 0 0;
    background: #FFF;
    box-shadow: 0 -4px 6px 2px rgba(0, 0, 0, 0.05);
    position: absolute;
    left: 0;
    right: 0;
    top: -45px;
    z-index: 6;

    @media (min-width: 992px) {
      padding: 20px;
      box-shadow: none;
      border-radius: 0;
      position: relative;
      top: 0;
      height: 100%;
      display: flex;
      flex-direction: column;
      padding-bottom: 0;
    }

    &__rectangle {
      width: 54px;
      height: 6px;
      background: #DFE0E0;
      border-radius: 20px;
      margin: -5px auto 15px;

      @media (min-width: 992px) {
        display: none;
      }
    }

    &__header {
      margin-bottom: 20px;
      flex: 0 0 auto;

      > div {
        display: flex;
        align-items: center;
        margin-bottom: 21px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__carrier {
      display: flex;
      align-items: center;
      gap: 16px;

      &-logo-container {
        width: 52px;
        height: 52px;
        border-radius: 2px;

        .carrier-logo {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        @media (min-width: 575px) {
          width: 70px;
          height: 70px;
        }
      }
    }

    &__tracking-number {
      gap: 2px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      color: #4E6574;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      flex: 1;

      .tracking-number {
        color: #163549;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        display: flex;
        align-items: center;
        gap: 27px;
        cursor: pointer;

        > svg {
          fill: #1C1C1C;
        }

        @media (min-width: 375px) {
          font-size: 14px;
        }

        @media (min-width: 575px) {
          font-size: 16px;
        }
      }
    }

    &__delivery_status_container {
      display: flex;
      padding: 15px 10px;
      align-items: center;
      gap: 16px;
      align-self: stretch;
      border-radius: 10px;
      background: #FAFAFA;

      .tracking-info__wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 6px;
        flex: 1;

        &__heading {
          color: #4E6574;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;

          @media (min-width: 375px) {
            font-size: 14px;
          }
        }

        &__description {
          color: #163549;
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;

          &.status-badge {
            color: #10B981;
            font-size: 14px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
          }

          @media (min-width: 375px) {
            font-size: 16px;

            &.status-badge {
              font-size: 16px;
            }
          }
        }
      }
    }

    &__delivery, &__status {
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-size: 14px;
      color: #5A5A5A;

      .delivery-date {
        font-weight: 600;
        color: #333333;
      }

      .status-badge {
        padding: 4px 12px;
        border-radius: 16px;
        font-size: 12px;
        font-weight: 500;

        &.out-for-delivery {
          background: #E8F5E8;
          color: #27AE60;
        }

        &.in-transit {
          background: #E3F2FD;
          color: #1976D2;
        }

        &.delivered {
          background: #E8F5E8;
          color: #27AE60;
        }

        &.pending {
          background: #FFF8E1;
          color: #F39C12;
        }
      }
    }

    &__timeline {
      margin-bottom: 33px;
      display: flex;
      flex-direction: column; /* allow nested scrollable child to expand */
      overflow: hidden;       /* prevent middle wrapper from overflowing */

      .timeline-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: #163549;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        margin-bottom: 17px;

        svg {
          transition: transform 0.2s;

          &.rotated {
            transform: rotate(180deg);
          }
        }
      }

      .timeline-content-wrapper {
        max-height: 500px;
        overflow-y: auto;
        transition: max-height 0.2s;
      }
    }

    &__actions {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 16px;
      width: 90%;
      margin-left: auto;
      margin-right: auto;
      position: relative;
      z-index: 2;
      flex: 0 0 auto;

      button {
        font-size: 18px;
      }

      .seemore {
        font-weight: 500;
        text-align: center;
        font-size: 14px;
        line-height: 100%;
        color: var(--theme_color);
        text-decoration: underline;

        @media (min-width: 575px) {
          display: none;
        }
      }

      @media (min-width: 575px) {
        max-width: 344px;
        width: 100%;
        margin-left: unset;
        margin-right: unset;
      }
    }
  }

  .timeline-container {
    max-height: 190px;
    overflow: hidden;

    &.show-all {
      max-height: unset;
      overflow: unset;
    }

    @media (min-width: 575px) {
      max-height: unset;
      overflow: auto;
      padding: 8px 0;
    }
  }

  .timeline {
    display: flex;
    flex-direction: column;
    position: relative;
  }

  .timeline-item {
    display: flex;
    position: relative;
    margin-bottom: 15px;

    .timeline-marker {
      position: relative;
      width: 20px;
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      top: 3px;
    }

    .timeline-line {
      position: absolute;
      top: 10px;
      left: 7px;
      height: 100%;
      border-left: 3px dashed #ccc;
      content: "";
      margin: 2px 0;
    }

    .timeline-line.completed {
      background-color: var(--theme_color);
    }

    .timeline-line.active {
      border-color: var(--theme_color);
    }

    .timeline-dot {
      width: 16px;
      height: 16px;
      background-color: #FFFFFF;
      border-radius: 50%;
      border: 3px solid #CCCCCC;
      z-index: 1;

      &.active {
        border-color: var(--theme_color);
      }

      &.completed {
        background-color: var(--theme_color);
      }
    }

    &:last-child {
      margin-bottom: 0;

      .timeline-line {
        height: 0;
      }
    }
  }

  .timeline-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 9px;
    padding-left: 15px;

    &__heading {
      gap: 1px;
      display: flex;
      flex-direction: column;
    }

    h3 {
      color: #163549;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      margin-bottom: 0;
    }

    .location {
      color: #163549;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      margin-bottom: 0;
    }

    .events {
      display: flex;
      flex-direction: column;
      gap: 4px;

      ul {
        padding: 0 15px;
        margin: 0;
      }
    }

    .event-time {
      color: #163549;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }

    .event-description {
      color: #4E6574;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
  }

  .map-container {
    width: 100%;
    height: 300px;
    border-radius: 0;
    overflow: hidden;
    position: relative;

    @media (min-width: 768px) {
      height: calc(100% - 110px);
      max-height: 768px;
    }
  }

  .map-container__location-marker {
    display: flex;
    flex-direction: column;
    height: 300px;
    color: var(--theme_color);
    position: absolute;
    z-index: 5;
    top: 0;
    left: 0;
    right: 0;
    align-items: center;
    padding: 10px 0 0;

    &:hover {
      background: unset;
    }

    @media (min-width: 768px) {
      height: 630px;
      padding: 100px 0 0;
    }

    svg {
      width: 250px;
      height: 120px;
      @media (min-width: 768px) {
        width: 350px;
        height: 150px;
      }
    }

    .marker-address {
      width: 200px;
      background: #FFFFFF;
      box-shadow: 0 4px 6px 2px rgba(0, 0, 0, 0.05);
      border-radius: 10px;
      margin-left: -70px;
      margin-bottom: 10px;
      padding: 15px;
      margin-right: 80px;

      @media (min-width: 768px) {
        margin-right: 200px;
      }

      .heading {
        font-weight: 500;
        font-size: 14px;
        line-height: 1.2;
        color: #163549;
        margin-bottom: 8px;

        @media (min-width: 575px) {
          font-size: 16px;
        }
      }

      .description {
        font-weight: 400;
        font-size: 12px;
        line-height: 100%;
        color: #4E6574;

        @media (min-width: 575px) {
          font-size: 14px;
        }
      }
    }
  }

  #map {
    width: 100%;
    height: 100%;
  }

  .unset-height {
    max-height: unset !important;
  }
</style>
